import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { money } from "../lib/api";

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateQty } = useStore();
  const items = cart.items || [];
  const threshold = cart.free_shipping_threshold_cents || 0;
  const remaining = threshold > 0 ? Math.max(0, threshold - cart.subtotal_cents) : 0;

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-ink/30 backdrop-blur-sm z-50"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCartOpen(false)}
          />
          <motion.aside
            data-testid="cart-drawer"
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 260 }}
            aria-label="Shopping cart"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-candy-soft">
              <h2 className="font-display font-semibold text-xl flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-candy" /> Your cozy bag
              </h2>
              <button data-testid="cart-drawer-close" onClick={() => setCartOpen(false)} aria-label="Close cart" className="p-2 rounded-full hover:bg-candy-soft transition-colors duration-200">
                <X className="w-5 h-5" />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-3 p-8 text-center">
                <span className="text-5xl" aria-hidden="true">🧶</span>
                <p className="font-display font-semibold text-lg">Your bag is empty</p>
                <p className="text-muted text-sm">Fill it with something soft and snuggly.</p>
                <Link to="/shop" data-testid="cart-drawer-shop-link" onClick={() => setCartOpen(false)} className="mt-2 px-6 py-3 rounded-full bg-candy text-white font-bold shadow-pink hover:bg-candy-deep transition-colors duration-200">
                  Browse creations
                </Link>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                  {threshold > 0 && (
                    <div className="bg-candy-mist rounded-2xl p-3" data-testid="free-shipping-progress">
                      <p className="text-xs font-bold text-ink mb-1.5">
                        {remaining > 0 ? `${money(remaining)} away from free shipping` : "You've unlocked free shipping!"}
                      </p>
                      <div className="h-2 rounded-full bg-white overflow-hidden">
                        <div className="h-full bg-candy rounded-full transition-all duration-500" style={{ width: `${Math.min(100, (cart.subtotal_cents / threshold) * 100)}%` }} />
                      </div>
                    </div>
                  )}
                  {items.map((it) => (
                    <div key={`${it.product_id}-${it.variant_label}`} className="flex gap-3 items-center bg-white rounded-2xl border border-candy-soft/60 p-2" data-testid={`cart-item-${it.slug}`}>
                      <img src={it.image} alt="" className="w-16 h-16 rounded-xl object-cover bg-candy-soft" />
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-semibold truncate">{it.title}</p>
                        {it.variant_label && <p className="text-xs text-muted font-semibold">{it.variant_label}</p>}
                        <p className="text-sm font-bold text-candy-deep">{money(it.price_cents)}</p>
                      </div>
                      <div className="flex items-center gap-1 bg-candy-mist rounded-full px-1 py-1">
                        <button data-testid={`cart-qty-minus-${it.slug}`} onClick={() => updateQty(it.product_id, it.variant_label, it.qty - 1)} aria-label="Decrease quantity" className="w-7 h-7 rounded-full bg-white flex items-center justify-center hover:bg-candy-glow transition-colors duration-200"><Minus className="w-3.5 h-3.5" /></button>
                        <span className="w-6 text-center font-bold text-sm" data-testid={`cart-qty-${it.slug}`}>{it.qty}</span>
                        <button data-testid={`cart-qty-plus-${it.slug}`} onClick={() => updateQty(it.product_id, it.variant_label, it.qty + 1)} aria-label="Increase quantity" className="w-7 h-7 rounded-full bg-white flex items-center justify-center hover:bg-candy-glow transition-colors duration-200 disabled:opacity-40" disabled={it.qty >= it.max_stock}><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <button data-testid={`cart-remove-${it.slug}`} onClick={() => updateQty(it.product_id, it.variant_label, 0)} aria-label={`Remove ${it.title}`} className="p-2 text-muted hover:text-candy-deep transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
                <div className="border-t border-candy-soft p-6 space-y-3 bg-candy-mist/50">
                  <div className="flex justify-between text-sm font-bold text-ink/70"><span>Subtotal</span><span data-testid="cart-drawer-subtotal">{money(cart.subtotal_cents)}</span></div>
                  <div className="flex justify-between text-sm font-bold text-ink/70"><span>Shipping</span><span>{cart.shipping_cents === 0 ? "Free!" : money(cart.shipping_cents)}</span></div>
                  <div className="flex justify-between font-display font-semibold text-lg"><span>Total</span><span data-testid="cart-drawer-total">{money(cart.total_cents)}</span></div>
                  <Link to="/checkout" data-testid="cart-drawer-checkout-button" onClick={() => setCartOpen(false)} className="block text-center w-full py-3.5 rounded-full bg-candy hover:bg-candy-deep text-white font-bold shadow-pink transition-colors duration-200">
                    Checkout <span className="opacity-80">· Stripe test mode</span>
                  </Link>
                  <Link to="/cart" data-testid="cart-drawer-view-cart-link" onClick={() => setCartOpen(false)} className="block text-center w-full py-2.5 rounded-full border-2 border-candy-soft font-bold text-ink hover:bg-candy-soft transition-colors duration-200 text-sm">
                    View full cart
                  </Link>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
